import { useState, useEffect, useCallback } from 'react';
import { getFontWithFallback, preloadFonts } from '../../../js/text/fontUtils';

/**
 * Hook for managing font loading and fallbacks
 */
export const useFontLoader = () => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [loadingFonts, setLoadingFonts] = useState(new Set());
  const [fontErrors, setFontErrors] = useState({});

  // Load a single font
  const loadFont = useCallback(
    async (fontName) => {
      if (loadedFonts.has(fontName)) {
        return fontName;
      }

      if (loadingFonts.has(fontName)) {
        // Wait for current loading to complete
        return new Promise((resolve) => {
          const checkLoaded = () => {
            if (loadedFonts.has(fontName)) {
              resolve(fontName);
            } else if (!loadingFonts.has(fontName)) {
              resolve(fontName); // Fallback
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        });
      }

      setLoadingFonts((prev) => new Set([...prev, fontName]));

      try {
        const fontWithFallback = await getFontWithFallback(fontName);
        setLoadedFonts((prev) => new Set([...prev, fontName]));
        setFontErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fontName];
          return newErrors;
        });
        return fontWithFallback;
      } catch (error) {
        setFontErrors((prev) => ({
          ...prev,
          [fontName]: error.message,
        }));
        return fontName; // Return original name as fallback
      } finally {
        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontName);
          return newSet;
        });
      }
    },
    [loadedFonts, loadingFonts],
  );

  // Load multiple fonts
  const loadFonts = useCallback(
    async (fontNames) => {
      const results = await Promise.allSettled(fontNames.map((fontName) => loadFont(fontName)));

      return results.map((result, index) => ({
        fontName: fontNames[index],
        success: result.status === 'fulfilled',
        value: result.status === 'fulfilled' ? result.value : fontNames[index],
        error: result.status === 'rejected' ? result.reason : null,
      }));
    },
    [loadFont],
  );

  // Check if font is loaded
  const isFontLoaded = useCallback(
    (fontName) => {
      return loadedFonts.has(fontName);
    },
    [loadedFonts],
  );

  // Check if font is loading
  const isFontLoading = useCallback(
    (fontName) => {
      return loadingFonts.has(fontName);
    },
    [loadingFonts],
  );

  // Get font error
  const getFontError = useCallback(
    (fontName) => {
      return fontErrors[fontName] || null;
    },
    [fontErrors],
  );

  // Preload all fonts on mount to ensure consistency across iOS and Android
  useEffect(() => {
    // Preload all fonts from FONT_FAMILIES to ensure consistent rendering
    const allFonts = ['Basic', 'Inter', 'Oswald'];
    preloadFonts(allFonts).then((loaded) => {
      setLoadedFonts(new Set(loaded));
    });
  }, []);

  return {
    loadFont,
    loadFonts,
    isFontLoaded,
    isFontLoading,
    getFontError,
    loadedFonts: Array.from(loadedFonts),
    loadingFonts: Array.from(loadingFonts),
    fontErrors,
  };
};
